/**
 * Maps canonical English species / taxonomic menu labels (stored in API & config) to numeric i18n keys.
 * Display with t(key); never persist translated text as species identifiers.
 */

import maps from './livestock-age-status-maps.json'

/** ANIMAL_OPTIONS category labels → i18n keys (see lib/am-batch2-en-batch.js). */
const SPECIES_MENU_CATEGORY_TO_KEY = Object.freeze({
    Aquatic: '4677',
    Insects: '4678',
    Birds: '4679',
    Camelids: '4680',
    Cervids: '4681',
    Equids: '4682',
    Ruminants: '4683',
    Suids: '4684',
})

/** Plural display names from SPECIES_CONFIGS / API → i18n keys (legacy keys reused where they exist). */
const SPECIES_ENGLISH_NAME_TO_KEY = Object.freeze({
    Cattle: '1813',
    Sheep: '1812',
    Goats: '4176',
    Pigs: '4175',
    Fish: '4685',
    Bees: '4686',
    Chickens: '4687',
    Ducks: '4688',
    Emus: '4689',
    Geese: '4690',
    Ostriches: '4691',
    Turkeys: '4692',
    Alpacas: '4693',
    Camels: '4694',
    Llamas: '4695',
    Deer: '4696',
    Horses: '4697',
})

/**
 * @param {string} englishLabel - Canonical English from API or ANIMAL_OPTIONS (e.g. "Cattle", "Ruminants")
 * @param {import('i18next').TFunction} t
 * @returns {string} Localized label, or original string if no mapping
 */
export function translateSpeciesUiLabel(englishLabel, t) {
    if (englishLabel == null || englishLabel === '') return ''
    const raw = String(englishLabel).trim()
    const speciesKey = SPECIES_ENGLISH_NAME_TO_KEY[raw]
    if (speciesKey) return t(speciesKey)
    const catKey = SPECIES_MENU_CATEGORY_TO_KEY[raw]
    if (catKey) return t(catKey)
    return raw
}

function normalizeLivestockToken(s) {
    return String(s ?? '')
        .trim()
        .toLowerCase()
}

function titleCaseWords(s) {
    return String(s ?? '').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
}

/**
 * Age-class tokens come from `species-ageclasses` keys (often lowercase, e.g. `heifer`, `neonate calf`).
 */
export function translateAgeClassLabel(raw, t) {
    if (raw == null || raw === '') return ''
    const n = normalizeLivestockToken(raw)
    const key = maps.ageNormToKey[n]
    if (key && typeof t === 'function') return t(key)
    return titleCaseWords(raw)
}

/** Canonical `detail.birth.gender` values from API / migrations. */
const GENDER_TOKEN_TO_KEY = Object.freeze({
    female: '2320',
    male: '2319',
    mixed: '2753',
})

/**
 * @param {string} raw - e.g. "Male", "Female", "Mixed"
 * @param {import('i18next').TFunction} t
 */
export function translateGenderLabel(raw, t) {
    if (raw == null || raw === '') return typeof t === 'function' ? t('1428') : ''
    const n = normalizeLivestockToken(raw)
    if (n === 'unknown') return typeof t === 'function' ? t('1428') : 'Unknown'
    const key = GENDER_TOKEN_TO_KEY[n]
    if (key && typeof t === 'function') return t(key)
    return String(raw).trim()
}

/**
 * Adjustment "removeAnimals" reasons are written verbatim into detail.health.status,
 * so a few reason tokens need aliasing to the canonical status used for display.
 */
const STATUS_TOKEN_ALIAS = Object.freeze({
    death: 'deceased',
})

/** Health / lifecycle status strings from `species-status` option lists. */
export function translateLivestockStatusLabel(raw, t) {
    if (raw == null || raw === '') return ''
    const token = normalizeLivestockToken(raw)
    const n = STATUS_TOKEN_ALIAS[token] ?? token
    const reuse = maps.statusReuse[n]
    if (reuse && typeof t === 'function') return t(reuse)
    const key = maps.statusNormToKey[n]
    if (key && typeof t === 'function') return t(key)
    return String(raw).trim()
}

/**
 * @param {string} fieldKey - `ageClasses`, `healthStatuses`, `breeds`, etc. from summarise-by buckets
 */
export function translateLivestockSummaryPillLabel(fieldKey, rawValue, t) {
    if (rawValue == null || rawValue === '') return ''
    if (fieldKey === 'ageClasses') return translateAgeClassLabel(rawValue, t)
    if (fieldKey === 'healthStatuses') return translateLivestockStatusLabel(rawValue, t)
    return String(rawValue)
}
